import { useContext, useState } from "react";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { AuthContext } from "../AuthContext";

export interface ContaReceberType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao: string;
    dataBaixa: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName: string;
    formaPagamentoId: number;
    formaPagamentoName: string;
    vendaId?: number | null;
    vendaBoletoId?: number | null;
    numeroParcela: number;
    totalParcelas: number;
    alunoNome?: string | null;
    enviadoSpc?: boolean;
    dataEnvioSpc?: string | null;
    permissaoVendaRestricao?: boolean;
}

export interface CreateContaReceberType {
    valor: number;
    taxa: number;
    vencimento: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    receiptAttachment?: string;
    empresaId: number;
    contaFinanceiraId: number;
    categoriaFinanceiraId: number;
    formaPagamentoId: number;
}

export interface UpdateContaReceberType {
    id: number;
    valor: number;
    taxa: number;
    vencimento: string;
    dataEmissao?: string;
    dataBaixa?: string;
    documento: string;
    codigoTransacao: number;
    historico: string;
    bandeira: string;
    receiptAttachment?: string;
    status: number;
    contaFinanceiraId: number;
    contaFinanceiraName?: string;
    categoriaFinanceiraId: number;
    categoriaFinanceiraName?: string;
    formaPagamentoId: number;
    formaPagamentoName?: string;
    hasReceiptAttachment?: boolean;
}

// Visão completa de uma parcela (GET /ContaReceber/{id}/detalhes). É a mesma projeção
// consumida pelo Portal de Cobrança via endpoint de integração.
export interface DetalhesAlunoType {
    id: number;
    referenceCode?: number | null;
    name: string;
    cpf?: string | null;
    email?: string | null;
    landline?: string | null;
    telephone_01?: string | null;
    telephone_02?: string | null;
    telephone_03?: string | null;
    telephone_04?: string | null;
    telephone_05?: string | null;
    telephone_06?: string | null;
    address?: string | null;
    addressNumber?: string | null;
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    zipCode?: string | null;
    facebook?: string | null;
    instagram?: string | null;
}

export interface DetalhesContratoType {
    id: number;
    description?: string | null;
    class?: string | null;
    status: number;
    statusDescricao?: string | null;
    hiredStudents?: number | null;
    currentStudents?: number | null;
    creationDate?: string | null;
    completionDate?: string | null;
    dateOfDischarge?: string | null;
    cursoNome?: string | null;
    instituicaoNome?: string | null;
    instituicaoCidade?: string | null;
    instituicaoEstado?: string | null;
    totalValue?: number | null;
    downPaymentValue?: number | null;
    installmentsCount?: number | null;
    installmentValue?: number | null;
    paymentDueDay?: number | null;
}

export interface DetalhesClienteType {
    id: number;
    name: string;
    dataNascimento?: string | null;
    cpf?: string | null;
    rg?: string | null;
    orgaoEmissor?: string | null;
    email?: string | null;
    telefone?: string | null;
    celular?: string | null;
    endereco?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    cep?: string | null;
    nacionalidade?: string | null;
    estadoCivil?: string | null;
    profissao?: string | null;
}

export interface DetalhesParcelaType {
    contaReceberId: number;
    numeroParcela: number;
    totalParcelas: number;
    vencimento: string;
    valor: number;
    valorPago?: number | null;
    status: number;
    statusDescricao: string;
    dataBaixa?: string | null;
    emAtraso: boolean;
    diasAtraso: number;
    formaPagamentoNome?: string | null;
    temBoleto: boolean;
    nossoNumero?: string | null;
    linhaDigitavel?: string | null;
}

export interface DetalhesFinanceiroType {
    vendaId?: number | null;
    produtoNome?: string | null;
    vendedorNome?: string | null;
    valorNegociado?: number | null;
    entradaValor: number;
    formasPagamento: { nome?: string | null; valor: number; boleto: boolean }[];
    quantidadeParcelas: number;
    parcelasPagas: number;
    parcelasPendentes: number;
    parcelasEmAtraso: number;
    valorTotal: number;
    valorPago: number;
    valorEmAberto: number;
    parcelas: DetalhesParcelaType[];
}

export interface ContaReceberDetalhesType {
    contaReceberId: number;
    permissaoVendaRestricao?: boolean;
    aluno?: DetalhesAlunoType | null;
    contrato?: DetalhesContratoType | null;
    cliente?: DetalhesClienteType | null;
    financeiro?: DetalhesFinanceiroType | null;
}

export interface EfetuarBaixaReceberRequest {
    contaReceberId: number;
    dataBaixa: string;
    valorPago: number;
    observacao?: string;
}

export default function useContaReceber() {
    const showToast = useToast();
    const { setLoading } = useContext(LoadingContext);
    const { handleLogout, objUserTk } = useContext(AuthContext);
    const [listContaReceber, setListContaReceber] = useState<ContaReceberType[]>([]);
    const [listContaReceberByEmpresa, setListContaReceberByEmpresa] = useState<ContaReceberType[]>([]);
    const [contaReceberPageCount, setContaReceberPageCount] = useState<number>(0);
    const [initialListContaReceber, setInitialListContaReceber] = useState<ContaReceberType[]>([]);
    const [contaReceber, setContaReceber] = useState<ContaReceberType>({
        id: 0,
        valor: 0,
        taxa: 0,
        vencimento: "",
        dataEmissao: "",
        dataBaixa: "",
        documento: "",
        codigoTransacao: 0,
        historico: "",
        bandeira: "",
        status: 0,
        contaFinanceiraId: 0,
        contaFinanceiraName: "",
        categoriaFinanceiraId: 0,
        categoriaFinanceiraName: "",
        formaPagamentoId: 0,
        formaPagamentoName: "",
        numeroParcela: 1,
        totalParcelas: 1,
    });
    const [contaReceberTotalValor, setContaReceberTotalValor] = useState<number>(0);

    function createContaReceber(createContaReceberType: CreateContaReceberType): Promise<CreateContaReceberType | null> {
        setLoading(true);
        return api
            .post(`/ContaReceber/create`, createContaReceberType)
            .then((response) => {
                const createdContaReceber: CreateContaReceberType = response.data;
                showToast("success", "Conta Receber criada com sucesso.");
                setLoading(false);
                return createdContaReceber;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao criar conta Receber. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao criar conta Receber:", error);
                return null;
            });
    }

    function getAllContaReceberPaged(
        pageNumber: number,
        pageSize: number,
        empresaId?: number,
        dataInicial?: string,
        dataFinal?: string,
        status?: number,
        codigoTransacao?: number,
        documento?: string,
        historico?: string,
        contaFinanceiraId?: number,
        categoriaFinanceiraId?: number,
        formaPagamentoId?: number,
        alunoId?: number
    ): Promise<ContaReceberType[] | null> {
        setLoading(true);
        let url = `/ContaReceber/getAllContaReceberPaged?pageNumber=${pageNumber}&pageSize=${pageSize}`;

        if (empresaId) {
            url += `&empresaId=${empresaId}`;
        }

        if (dataInicial) {
            url += `&dataInicial=${dataInicial}`;
        }

        if (dataFinal) {
            url += `&dataFinal=${dataFinal}`;
        }

        if (status != undefined) {
            url += `&status=${status}`;
        }

        if (codigoTransacao !== undefined && codigoTransacao > 0) {
            url += `&codigoTransacao=${codigoTransacao}`
        }

        if (documento) {
            url += `&documento=${documento}`;
        }

        if (historico) {
            url += `&historico=${historico}`;
        }

        if (contaFinanceiraId !== undefined && contaFinanceiraId > 0) {
            url += `&contaFinanceiraId=${contaFinanceiraId}`;
        }

        if (categoriaFinanceiraId !== undefined && categoriaFinanceiraId > 0) {
            url += `&categoriaFinanceiraId=${categoriaFinanceiraId}`;
        }

        if (formaPagamentoId !== undefined && formaPagamentoId > 0) {
            url += `&formaPagamentoId=${formaPagamentoId}`;
        }

        if (alunoId !== undefined && alunoId > 0) {
            url += `&alunoId=${alunoId}`;
        }

        return api
            .get(url)
            .then((response) => {
                setListContaReceber(response.data.result);
                setInitialListContaReceber(response.data.result);
                setContaReceberPageCount(response.data.pageCount);
                setContaReceberTotalValor(response.data.totalValor);
                setLoading(false);
                return response.data.result;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao carregar contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao carregar contas do financeiro:", error);
                return null;
            });
    }

    function updateContaReceber(updateContaReceberData: UpdateContaReceberType): Promise<UpdateContaReceberType | null> {
        setLoading(true);
        return api
            .put(`/ContaReceber/${updateContaReceberData.id}`, updateContaReceberData)
            .then((response) => {
                showToast("success", "Conta atualizada com sucesso.");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao atualizar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao atualizar conta:", error);
                return null;
            });
    }

    function deleteContaReceber(contaReceberId: number): Promise<ContaReceberType | null> {
        setLoading(true);
        return api
            .delete(`/ContaReceber/${contaReceberId}`)
            .then(() => {
                setContaReceber({
                    id: 0,
                    valor: 0,
                    taxa: 0,
                    vencimento: "",
                    dataEmissao: "",
                    dataBaixa: "",
                    documento: "",
                    codigoTransacao: 0,
                    historico: "",
                    bandeira: "",
                    status: 0,
                    contaFinanceiraId: 0,
                    contaFinanceiraName: "",
                    categoriaFinanceiraId: 0,
                    categoriaFinanceiraName: "",
                    formaPagamentoId: 0,
                    formaPagamentoName: "",
                    numeroParcela: 1,
                    totalParcelas: 1,
                });
                showToast("success", "Conta deletada com sucesso.");
                setLoading(false);
                return null;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao deletar conta. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao deletar conta:", error);
                return null;
            });
    }

    function handleGetContaReceberByEmpresaId(empresaId: number): Promise<ContaReceberType[] | null> {
        setLoading(true);
        return api
            .get(`/ContaReceber/getContaReceberByEmpresaId?empresaId=${empresaId}`)
            .then((response) => {
                const contaReceber: ContaReceberType[] = response.data;
                setListContaReceberByEmpresa(contaReceber);
                setLoading(false);
                return contaReceber;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao buscar informações das contas do financeiro. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao buscar informações das contas do financeiro:", error);
                return null;
            });
    }

    function efetuarBaixaContaReceber(request: EfetuarBaixaReceberRequest): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaReceber/efetuar-baixa`, request)
            .then(() => {
                showToast("success", "Baixa efetuada com sucesso.");
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao efetuar baixa. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao efetuar baixa da conta a receber:", error);
                return false;
            });
    }

    function efetuarBaixaLoteContaReceber(contaReceberIds: number[], dataBaixa: string): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaReceber/efetuar-baixa-lote`, { contaReceberIds, dataBaixa })
            .then(() => {
                showToast("success", `Baixa efetuada em ${contaReceberIds.length} conta(s).`);
                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast(
                        "error",
                        "Sessão desconectada. Favor login novamente.",
                        error
                    );
                } else {
                    showToast(
                        "error",
                        "Erro ao efetuar baixa em lote. Verifique novamente mais tarde.",
                        error
                    );
                }
                setLoading(false);
                console.error("Erro ao efetuar baixa em lote:", error);
                return false;
            });
    }

    function getContaReceberDetalhes(contaReceberId: number): Promise<ContaReceberDetalhesType | null> {
        setLoading(true);
        return api
            .get(`/ContaReceber/${contaReceberId}/detalhes`)
            .then((response) => {
                setLoading(false);
                return response.data as ContaReceberDetalhesType;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else if (error?.response?.status === 404) {
                    showToast("warning", "Esta conta não tem aluno/venda vinculados para detalhar.");
                } else {
                    showToast("error", "Erro ao buscar os detalhes da conta.", error);
                }
                setLoading(false);
                console.error("Erro ao buscar detalhes da conta a receber:", error);
                return null;
            });
    }

    // Baixa o PDF dos boletos das parcelas informadas. formato "pdf" gera um arquivo único
    // (uma página por boleto) e "zip" gera um PDF por parcela.
    function baixarBoletos(
        contaReceberIds: number[],
        formato: "pdf" | "zip",
        nomeArquivo: string
    ): Promise<boolean> {
        setLoading(true);
        return api
            .post(`/ContaReceber/boletos`, { contaReceberIds, formato }, { responseType: "blob" })
            .then((response) => {
                const tipo = formato === "zip" ? "application/zip" : "application/pdf";
                const blob = new Blob([response.data], { type: tipo });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = nomeArquivo;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                const semBoleto = Number(response.headers["x-boletos-sem-boleto"] ?? 0);
                if (semBoleto > 0) {
                    showToast(
                        "warning",
                        `${semBoleto} parcela(s) selecionada(s) não possuem boleto emitido e foram ignoradas.`
                    );
                } else {
                    showToast("success", "Boleto(s) baixado(s) com sucesso.");
                }

                setLoading(false);
                return true;
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    handleLogout();
                    showToast("error", "Sessão desconectada. Favor login novamente.", error);
                } else if (error?.response?.status === 404) {
                    showToast("error", "Nenhuma das parcelas selecionadas possui boleto emitido.");
                } else {
                    showToast("error", "Erro ao baixar boleto(s). Verifique novamente mais tarde.", error);
                }
                setLoading(false);
                console.error("Erro ao baixar boletos:", error);
                return false;
            });
    }

    function sincronizarRestricoes(): Promise<{ sucesso: boolean; registrosAtualizados: number; mensagem: string } | null> {
        setLoading(true);
        return api
            .post(`/ContaReceber/sync-restricoes`)
            .then((response) => {
                showToast("success", response.data?.mensagem || "Sincronização de restrições concluída com sucesso!");
                setLoading(false);
                return response.data;
            })
            .catch((error) => {
                showToast("error", "Erro ao sincronizar restrições. Tente novamente mais tarde.", error);
                setLoading(false);
                console.error("Erro ao sincronizar restrições:", error);
                return null;
            });
    }

    return {
        createContaReceber,
        getAllContaReceberPaged,
        updateContaReceber,
        deleteContaReceber,
        setListContaReceber,
        listContaReceber,
        setContaReceberPageCount,
        contaReceberPageCount,
        setInitialListContaReceber,
        initialListContaReceber,
        setContaReceber,
        contaReceber,
        handleGetContaReceberByEmpresaId,
        setListContaReceberByEmpresa,
        listContaReceberByEmpresa,
        efetuarBaixaContaReceber,
        efetuarBaixaLoteContaReceber,
        baixarBoletos,
        getContaReceberDetalhes,
        contaReceberTotalValor,
        setContaReceberTotalValor,
        sincronizarRestricoes,
    };
}